import { AvailableLanguages } from '@repo/toolkit/language-types'
import { z } from 'zod'

import { t } from '../../../locale/utils'
type NumberSchemaOptions = {
  errorMessage?: string
  isPositive?: boolean
  isInteger?: boolean
  minimumValue?: number
  maximumValue?: number
  nonNegative?: boolean
  isAtLeastZero?: boolean
  decimalPlaces?: number
  language?: AvailableLanguages
} & ({ optional?: boolean; nullish?: undefined } | { nullish?: boolean; optional?: undefined })

export const createNumberSchema = <T extends boolean | undefined = true>(
  fieldName: string,
  options?: NumberSchemaOptions
): T extends true
  ? z.ZodNumber
  : z.ZodOptional<z.ZodNumber> | z.ZodOptional<z.ZodNullable<z.ZodNumber>> => {
  const optional = options?.optional ?? false
  let schema: z.ZodNumber

  const lng = options?.language

  schema = z.coerce.number({
    required_error: options?.errorMessage ?? t('errors.number.required', { fieldName, lng }),
    invalid_type_error: options?.errorMessage ?? t('errors.number.invalid', { fieldName, lng })
  })

  const isAtLeastZero = options?.isAtLeastZero || options?.isPositive

  if (options?.isPositive && !isAtLeastZero)
    schema = schema.positive({
      message: t('errors.number.positive', { fieldName, lng })
    })

  if (options?.isInteger)
    schema = schema.int({
      message: t('errors.number.integer', { fieldName, lng })
    })

  if (options?.minimumValue)
    schema = schema.min(options.minimumValue, {
      message: t('errors.number.min', { fieldName, minimum: options.minimumValue, lng })
    })

  if (options?.maximumValue)
    schema = schema.max(options.maximumValue, {
      message: t('errors.number.max', { fieldName, maximum: options.maximumValue, lng })
    })

  if (options?.nonNegative || isAtLeastZero)
    schema = schema.nonnegative({
      message: t('errors.number.nonNegative', { fieldName, lng })
    })

  if (options?.decimalPlaces !== undefined)
    schema = schema.multipleOf(Math.pow(10, -options.decimalPlaces), {
      message: t('errors.number.decimalPlaces', {
        fieldName,
        decimalPlaces: options.decimalPlaces,
        lng
      })
    })

  if (optional) {
    return schema.optional() as T extends true ? z.ZodNumber : z.ZodOptional<z.ZodNumber>
  }
  if (options?.nullish)
    return schema.nullish() as T extends true
      ? z.ZodNumber
      : z.ZodOptional<z.ZodNullable<z.ZodNumber>>

  return schema as T extends true ? z.ZodNumber : z.ZodOptional<z.ZodNumber>
}

type NumberArraySchemaOptions = NumberSchemaOptions & {
  errorMessage?: string
  minimumNumberOfItems?: number
  language?: AvailableLanguages
}

export const createNumberArraySchema = (fieldName: string, options?: NumberArraySchemaOptions) => {
  const lng = options?.language

  const baseSchema = createNumberSchema(fieldName, options)

  let arraySchema = z.array(baseSchema, {
    required_error: options?.errorMessage ?? t('errors.number.list.required', { fieldName, lng })
  })

  if (options?.minimumNumberOfItems)
    arraySchema = arraySchema.min(options.minimumNumberOfItems, {
      message:
        options?.errorMessage ??
        t('errors.number.list.min', {
          fieldName,
          minimumNumberOfItems: options.minimumNumberOfItems,
          lng
        })
    })

  return arraySchema
}
